import type { ChatMessageDTO } from '@/api/agentApi';
import type { ProjectPhasePlanItem } from '@/api/projectApi';

export type ProjectCreateFormSnapshot = {
  name: string;
  projectCode?: string;
  owner?: string;
  description?: string;
  projectDeliveryDate: string;
  projectLeadUserId?: number;
  projectMemberUserIds: number[];
};

export type ProjectCreateDraftSnapshotV1 = {
  version: 1;
  draftKey: string;
  sessionId: string;
  createRequestId?: string;
  lastSubmittedPayloadFingerprint?: string;
  updatedAt: number;
  form: ProjectCreateFormSnapshot;
  phasePlans: ProjectPhasePlanItem[];
  aiStructuredResult: Record<string, unknown> | null;
  aiProjectGoal: string;
  aiProjectType: string;
  aiProjectRisks: string[];
  /** 大模型建议风险等级（详情页「项目风险」方向），可选 */
  aiSuggestedRiskLevel?: string;
  /** 与右侧 AI 会话同步的副本，用于关闭页签时无需依赖当前挂载的页面即可保存到后端 */
  messagesSnapshot?: ChatMessageDTO[];
  /** 供 Word 打开的 HTML 文档（仅在用户确认「保存草稿」时生成并写入） */
  wordSnapshotHtml?: string;
};

function escHtml(s: string): string {
  return String(s ?? '')
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;');
}

/** 生成可用 Word 打开的 HTML 快照（另存为 .doc 或直接用 Word 打开） */
export function buildProjectCreateWordSnapshotHtml(payload: {
  projectTitle: string;
  form: ProjectCreateFormSnapshot;
  phasePlans: ProjectPhasePlanItem[];
  aiProjectGoal: string;
  aiProjectType: string;
  aiProjectRisks: string[];
  aiSuggestedRiskLevel?: string;
  savedAtIso: string;
}): string {
  const title = (payload.projectTitle || '新项目').trim();
  const rows: [string, string][] = [
    ['项目名称', payload.form.name || '—'],
    ['项目编号', payload.form.projectCode || '—'],
    ['客户名称', payload.form.owner || '—'],
    ['项目简介', payload.form.description || '—'],
    ['总体交付日期', payload.form.projectDeliveryDate || '—'],
    ['建议风险等级（AI）', (payload.aiSuggestedRiskLevel || '').trim() || '—'],
    ['项目负责人 userId', payload.form.projectLeadUserId != null ? String(payload.form.projectLeadUserId) : '—'],
    ['项目成员 userIds', (payload.form.projectMemberUserIds || []).join('、') || '—'],
    ['项目目标（AI）', payload.aiProjectGoal || '—'],
    ['项目类型（AI）', payload.aiProjectType || '—'],
    ['风险要点（AI）', (payload.aiProjectRisks || []).join('；') || '—'],
    ['快照时间', payload.savedAtIso],
  ];
  const phaseRows = (payload.phasePlans || []).map((p) => {
    const end = p.plannedEndDate || '—';
    const owner = p.phaseOwnerUserId != null ? String(p.phaseOwnerUserId) : '—';
    return `<tr><td>${escHtml(p.phaseName)}</td><td>${escHtml(p.phaseCode)}</td><td>${escHtml(owner)}</td><td>${escHtml(end)}</td></tr>`;
  });
  const tableBody = rows
    .map(([k, v]) => `<tr><th align="left">${escHtml(k)}</th><td>${escHtml(v)}</td></tr>`)
    .join('');
  return `<!DOCTYPE html>
<html xmlns:o="urn:schemas-microsoft-com:office:office" xmlns:w="urn:schemas-microsoft-com:office:word">
<head>
<meta charset="utf-8">
<title>${escHtml(title)} — 项目创建草稿</title>
<style>
body { font-family: "Microsoft YaHei", "PingFang SC", sans-serif; font-size: 11pt; color: #1f2329; }
h1 { font-size: 16pt; color: #0052d9; }
table.info { border-collapse: collapse; width: 100%; margin: 12px 0; }
table.info th, table.info td { border: 1px solid #d0d3d6; padding: 6px 10px; }
table.phases { border-collapse: collapse; width: 100%; margin-top: 16px; }
table.phases th, table.phases td { border: 1px solid #d0d3d6; padding: 6px 10px; font-size: 10pt; }
</style>
</head>
<body>
<h1>${escHtml(title)} — 项目创建 · 表单快照</h1>
<p>本文件由系统在保存草稿时生成，可用 Microsoft Word 直接打开继续编辑。</p>
<table class="info">${tableBody}</table>
<h2>阶段计划</h2>
<table class="phases">
<tr><th>阶段名称</th><th>编码</th><th>负责人 userId</th><th>计划结束日</th></tr>
${phaseRows.length ? phaseRows : '<tr><td colspan="4">（暂无）</td></tr>'}
</table>
</body>
</html>`;
}
